import { useEffect, useState } from "react";
import { API } from "../lib/supabase";
import { loadJob, saveJob, queuedFor, applyQueue, prefetchForOffline } from "../lib/contractorOffline";
import { claimSecret } from "../lib/claim";

export const useRobotsMeta = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow, noarchive, nosnippet"; m.dataset.dropit = "1";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
};

export const usePublicDispatch = (token) => {
  const [job, setJob] = useState(undefined);
  const [meta, setMeta] = useState({ source: "network", saved: false, offlineNoCache: false, expired: false });
  useEffect(() => {
    let alive = true;
    (async () => {
      let d = null, fromNetwork = false, serverExpired = false;
      try {
        const rec = await claimSecret(token).catch(() => null);
        const r = await fetch(`${API}/public/dispatch/${token}`, { cache: "no-store", headers: { "X-Claim-Hash": rec?.hash || "" } });
        if (r.ok) { d = await r.json(); fromNetwork = r.headers.get("x-sw-offline") !== "1"; }
        else if (r.status === 404) {  // expired/purged upstream → mark the saved copy read-only (and remember it)
          const cached = await loadJob(token);
          if (cached) { d = { ...cached, expires_at: new Date(Math.min(Date.now(), new Date(cached.expires_at || Date.now()))).toISOString() }; serverExpired = true; await saveJob(token, d); }
        }
      } catch { d = await loadJob(token); }
      if (!alive) return;
      const queued = d ? await queuedFor(token) : [];
      if (d && !fromNetwork) {
        const saved = await loadJob(token);  // IDB copy remembers a server-side expiry seen earlier
        const expired = serverExpired || (d.expires_at && new Date(d.expires_at) < new Date()) || (saved?.expires_at && new Date(saved.expires_at) < new Date());
        setMeta({ source: "cache", saved: true, offlineNoCache: false, expired });
        setJob(applyQueue(d, queued));
        return;
      }
      if (!d) { setMeta((m) => ({ ...m, offlineNoCache: !navigator.onLine })); setJob(null); return; }
      setJob(applyQueue(d, queued));
      await saveJob(token, d);
      setMeta({ source: "network", saved: false, offlineNoCache: false, expired: false });
      prefetchForOffline(d).then(() => alive && setMeta((m) => ({ ...m, saved: true })));
    })();
    return () => { alive = false; };
  }, [token]);
  return [job, setJob, meta, setMeta];
};

export const useChecklist = (token, n) => {
  const key = `dropit-checks-${token}`;
  const [checks, setChecks] = useState(() => { try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; } });
  const toggle = (i) => setChecks((c) => { const next = [...c]; next[i] = !next[i]; localStorage.setItem(key, JSON.stringify(next)); return next; });
  return [checks.slice(0, n), toggle];
};

export const useTranslation = (job, lang) => {
  const [state, setState] = useState({ busy: false, data: null, show: false });
  const needs = job && job.language && job.language.slice(0, 2).toLowerCase() !== lang;
  const run = async () => {
    if (state.data) return setState((s) => ({ ...s, show: !s.show }));
    const key = `dropit-tr-${job.job_token}-${lang}`;
    const cached = sessionStorage.getItem(key);
    if (cached) return setState({ busy: false, data: JSON.parse(cached), show: true });
    setState((s) => ({ ...s, busy: true }));
    const payload = { title: job.title, tasks: job.tasks || [], hazards: job.hazards || [], transcript: job.transcript || "" };
    const r = await fetch(`${API}/public/dispatch/${job.job_token}/translate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ target_language: lang, payload }) });
    if (!r.ok) return setState((s) => ({ ...s, busy: false }));
    const data = await r.json();
    sessionStorage.setItem(key, JSON.stringify(data));
    setState({ busy: false, data, show: true });
  };
  return { needs, ...state, run };
};
